import React, { useState, useEffect, useCallback } from "react";
import { useAsync } from "./hooks";
import { api } from "./api";
import { fmtDate, fmtMoney, SC, I } from "./utils";
import { Btn, Card, Icon, Spinner, ApiError, Badge, StatCard, PieChart } from "./ui";
import { BRAND_HEX, BRAND_LIGHT, BRAND_RING, B } from "./theme";  // ← single import
import { useAuth } from "./auth";

import { BRAND } from "./brand";

// ─── SUPPORTED CURRENCIES ────────────────────────────────────────────────────
const CURRENCIES = [
  "USD", "NGN", "GBP", "EUR",
  "GHS", "KES", "ZAR", "AED", "CAD", "AUD",
];

// ─── TIME-BASED GREETING ──────────────────────────────────────────────────────
// Returns "Good morning" / "Good afternoon" / "Good evening" from the local hour.
function getGreeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

// ─── CURRENCY SWITCHER ────────────────────────────────────────────────────────
function CurrencySwitcher({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3 py-1.5">
      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 select-none">
        Currency
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer"
      >
        {CURRENCIES.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    </div>
  );
}

export function DashboardTab({ onNavigate }) {
  // ── General stats (not currency-dependent) ────────────────────────────────
  const {
    data: stats,
    loading: sLoad,
    error: sErr,
    refetch: sRefetch,
  } = useAsync(() => api.get("/crm/dashboard/stats"));

  // ── Finance summary (currency-dependent) ─────────────────────────────────
  const [activeCurrency, setActiveCurrency] = useState(BRAND.currency);
  const [fin,    setFin]    = useState(null);
  const [fLoad,  setFLoad]  = useState(true);
  const [fError, setFError] = useState(null);

  const fetchFinance = useCallback(async () => {
    setFLoad(true);
    setFError(null);
    try {
      const data = await api.get(`/crm/finances/summary?currency=${activeCurrency}`);
      setFin(data);
    } catch (e) {
      setFError(e.message);
    } finally {
      setFLoad(false);
    }
  }, [activeCurrency]);

  useEffect(() => { fetchFinance(); }, [fetchFinance]);

  // ── Time-based greeting ─────────────────────────────────────────────────────
  // Computed once per mount; if you keep the dashboard open across a boundary
  // (e.g. morning → afternoon) and want it to update live, see the note below.
  const greeting = getGreeting();
  // Greet whoever is signed in: "osita@kaijego.com" → "Osita".
  const { user } = useAuth() || {};
  const handle = (user || "").split("@")[0].split(/[._-]/)[0];
  const firstName = handle ? handle.charAt(0).toUpperCase() + handle.slice(1) : "there";

  // ── Derived ───────────────────────────────────────────────────────────────
  const totalIncome   = fin?.totalIncome   ?? 0;
  const totalExpenses = fin?.totalExpenses ?? 0;
  const grossProfit   = fin?.grossProfit   ?? 0;
  // Where the money actually came from / went. See getFinanceSummary in crm.service.
  const bd            = fin?.breakdown     ?? {};
  const fmtC          = (v) => fmtMoney(v ?? 0, activeCurrency);

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            {greeting}, {firstName}
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">Here's what's happening today</p>
        </div>
        <Btn variant="primary" onClick={() => onNavigate("clients")}>
          <Icon d={I.plus} size={16} /> Add Client
        </Btn>
      </div>

      {sErr && <ApiError msg={sErr} onRetry={sRefetch} />}

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: "Total Clients",  value: stats?.totalClients,  icon: "clients", color: B.bg,             nav: "clients" },
          { label: "Active Trips",   value: stats?.activeTrips,   icon: "trips",   color: "bg-sky-500",     nav: "trips"   },
          { label: "Pending Tasks",  value: stats?.pendingTasks,  icon: "tasks",   color: "bg-violet-500",  nav: "tasks",  sub: "Due this week"   },
          { label: "Overdue Tasks",  value: stats?.overdueTasks,  icon: "bell",    color: "bg-rose-500",    nav: "tasks",  sub: "Needs attention" },
          { label: "New Leads",      value: stats?.newLeads,      icon: "leads",   color: "bg-emerald-500", nav: "leads"   },
        ].map((s) => (
          <div
            key={s.label}
            onClick={() => onNavigate(s.nav)}
            className="cursor-pointer hover:shadow-lg active:scale-[0.98] transition-all rounded-xl overflow-hidden"
          >
            <StatCard label={s.label} value={s.value} icon={s.icon} color={s.color} sub={s.sub} />
          </div>
        ))}
      </div>

      {/* ── Today's Tasks + Upcoming Trips ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-700 text-base">Open Tasks</h3>
            <Btn variant="ghost" size="sm" onClick={() => onNavigate("tasks")}>View all</Btn>
          </div>
          {sLoad ? <Spinner /> : !stats?.todayTasks?.length ? (
            <p className="text-sm text-slate-400 text-center py-6">No open tasks 🎉</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {stats.todayTasks.map((t) => (
                <div
                  key={t._id}
                  onClick={() => onNavigate("tasks")}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors cursor-pointer"
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    t.priority === "high"   ? "bg-rose-500"
                    : t.priority === "medium" ? "bg-amber-400"
                    : "bg-emerald-400"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{t.taskTitle}</p>
                    <p className="text-xs text-slate-400">{t.clientId?.fullName}</p>
                  </div>
                  <Badge label={t.status} colorClass={SC[t.status] || "bg-slate-100 text-slate-500"} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-700 text-base">Upcoming Trips</h3>
            <Btn variant="ghost" size="sm" onClick={() => onNavigate("trips")}>View all</Btn>
          </div>
          {sLoad ? <Spinner /> : !stats?.upcomingTrips?.length ? (
            <p className="text-sm text-slate-400 text-center py-6">No upcoming trips</p>
          ) : (
            <div className="space-y-2">
              {stats.upcomingTrips.map((t) => (
                <div
                  key={t._id}
                  onClick={() => onNavigate("trips")}
                  className="p-3 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold text-slate-700">{t.tripName}</p>
                    <Badge label={t.status} colorClass={SC[t.status] || "bg-slate-100 text-slate-500"} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Icon d={I.calendar} size={12} /> {fmtDate(t.startDate)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon d={I.clients} size={12} /> {t.membersCount ?? 0} pax
                    </span>
                    <span>{t.destinations?.join(", ")}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ── Finance Snapshot ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Pie chart card */}
        <Card className="p-5 flex flex-col items-center">
          <div className="flex items-center justify-between w-full mb-4">
            <h3 className="font-bold text-slate-700 text-base">Finance Snapshot</h3>
            <CurrencySwitcher value={activeCurrency} onChange={setActiveCurrency} />
          </div>
          {fLoad ? (
            <div className="flex-1 flex items-center justify-center py-6"><Spinner /></div>
          ) : fError ? (
            <div className="flex-1 flex flex-col items-center justify-center py-6 gap-2">
              <p className="text-xs text-rose-400">{fError}</p>
              <Btn variant="ghost" size="sm" onClick={fetchFinance}>Retry</Btn>
            </div>
          ) : (
            <>
              <PieChart income={totalIncome} expenses={totalExpenses} />
              <div className="flex gap-4 justify-center text-xs mt-2">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Income
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-rose-500 inline-block" /> Expense
                </span>
              </div>
              {totalIncome === 0 && totalExpenses === 0 && (
                <p className="text-xs text-slate-400 mt-3 text-center">
                  No {activeCurrency} records yet
                </p>
              )}
            </>
          )}
        </Card>

        {/* Revenue bars card */}
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-slate-700 text-base">Revenue Overview</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-0.5">
                {activeCurrency}
              </p>
            </div>
            <CurrencySwitcher value={activeCurrency} onChange={setActiveCurrency} />
          </div>

          {fLoad ? <Spinner /> : fError ? (
            <ApiError msg={fError} onRetry={fetchFinance} />
          ) : (
            <div className="space-y-3">
              {[
                {
                  label: "Total Income",
                  value: totalIncome,
                  color: "bg-emerald-500",
                  pct: 100,
                  sub: `Finance records ${fmtC(bd.financeIncome)} · Client payments ${fmtC(bd.clientPayments)}`,
                },
                {
                  label: "Total Expenses",
                  value: totalExpenses,
                  color: "bg-rose-400",
                  pct: totalIncome ? Math.round((totalExpenses / totalIncome) * 100) : 0,
                  sub: `Finance records ${fmtC(bd.financeExpenses)} · Refunds paid ${fmtC(bd.refunds)}`,
                },
                {
                  label: "Gross Profit",
                  value: grossProfit,
                  // ↓ brand color when positive, red when negative — comes from theme
                  color: grossProfit >= 0 ? B.bg : "bg-rose-600",
                  pct: totalIncome ? Math.round((grossProfit / totalIncome) * 100) : 0,
                  sub: "Income − expenses",
                },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500 font-medium">{item.label}</span>
                    <span className="font-bold text-slate-700">
                      {fmtMoney(item.value, activeCurrency)}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${item.color} transition-all duration-700`}
                      style={{ width: `${Math.max(0, Math.min(100, item.pct))}%` }}
                    />
                  </div>
                  {item.sub && (
                    <p className="text-[10px] text-slate-400 mt-1">{item.sub}</p>
                  )}
                </div>
              ))}

              {/* Kai Credit is not cash — shown for visibility, excluded from totals above. */}
              {bd.kaiCreditIssued > 0 && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-purple-50 border border-purple-100">
                  <span className="text-[11px] font-bold text-purple-600">{`${BRAND.creditName} issued`}<span className="font-normal text-purple-400"> · not cash</span>
                  </span>
                  <span className="text-xs font-black text-purple-600">
                    {fmtC(bd.kaiCreditIssued)}
                  </span>
                </div>
              )}

              <div className="pt-3 flex justify-end border-t border-slate-100">
                <Btn variant="ghost" size="sm" onClick={() => onNavigate("finances")}>
                  View Finances →
                </Btn>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}